import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Heart, Plus, X, CheckCircle2, XCircle, Clock, Upload } from 'lucide-react';
import { formatDate } from '../../lib/format';
import EmployeePicker from '../../components/EmployeePicker';
import { useAuth } from '../../contexts/AuthContext';

const RELATIONS = ['Self', 'Brother', 'Sister', 'Son', 'Daughter'];
const STATUS_COLORS = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function MarriageGift() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ employee_id: '', relationship: 'Self', wedding_date: '', relative_name: '', spouse_employee_id: '', wedding_card_url: '', notes: '' });
  const [uploading, setUploading] = useState(false);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isHRAdmin = role === 'super_admin' || role === 'admin' || dept === 'HR' || dept === 'MD' || role === 'hr' || role === 'md';

  const load = async () => {
    try {
      const d = await apiGet('/hr/benefits/marriage');
      setClaims(d.claims || []);
    } catch (err) { toast.error('Failed to load claims'); }
  };
  useEffect(() => { load(); apiGet('/hr/benefits/policy').then(setPolicy).catch(() => {}); }, []);

  const uploadCard = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/benefits/marriage/upload-card`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Upload failed');
      setForm(f => ({ ...f, wedding_card_url: data.url }));
      toast.success('Wedding card uploaded');
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.wedding_date) { toast.error('Employee and wedding date required'); return; }
    if (form.relationship !== 'Self' && !form.relative_name) { toast.error('Relative name required'); return; }
    try {
      const payload = { ...form };
      if (!payload.spouse_employee_id) delete payload.spouse_employee_id;
      if (!payload.relative_name) delete payload.relative_name;
      await apiPost('/hr/benefits/marriage', payload);
      toast.success('Claim submitted for HR approval');
      setShowForm(false);
      setForm({ employee_id: '', relationship: 'Self', wedding_date: '', relative_name: '', spouse_employee_id: '', wedding_card_url: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const decide = async (id, action) => {
    try {
      await apiPut(`/hr/benefits/marriage/${id}/${action}`, {});
      toast.success(`Claim ${action === 'approve' ? 'approved' : 'rejected'}`);
      load();
    } catch (err) { toast.error('Failed'); }
  };

  const amount = form.relationship === 'Self' ? (policy?.marriage_gift?.self_amount || 11000) : (policy?.marriage_gift?.relative_amount || 1001);

  return (
    <div className="p-6 md:p-8" data-testid="marriage-gift-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People &amp; HR · Benefits</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Marriage Gift Claims</h1>
          <p className="text-sm text-slate-600 mt-1">
            ₹11,000 for the employee's own marriage · ₹1,001 for a blood relative (Brother / Sister / Son / Daughter) · Once-only per employee.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-marriage-claim-btn">
          <Plus size={15} /> New Claim
        </button>
      </div>

      {policy && (
        <div className="mb-6 bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-sm p-5 flex items-start gap-3" data-testid="marriage-policy-banner">
          <Heart size={20} className="text-rose-600 shrink-0 mt-1" />
          <div className="text-xs text-slate-700">
            <div className="font-display font-bold text-sm text-[#0F172A] mb-1">Marriage Gift Policy v1</div>
            <ul className="space-y-0.5">
              <li>· Employee's own marriage → <strong>₹{policy.marriage_gift.self_amount.toLocaleString('en-IN')}</strong></li>
              <li>· Blood relative (Brother/Sister/Son/Daughter) → <strong>₹{policy.marriage_gift.relative_amount.toLocaleString('en-IN')}</strong></li>
              <li>· If both spouses work at ELR Industries, <strong>both employees</strong> receive the gift.</li>
              <li>· {policy.marriage_gift.frequency}.</li>
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Employee</th><th>Relationship</th><th>Wedding Date</th><th>Amount</th><th>Status</th><th>Submitted</th><th></th>
          </tr></thead>
          <tbody>
            {claims.length === 0 && <tr><td colSpan="7" className="text-center p-8 text-sm text-slate-500">No marriage gift claims yet.</td></tr>}
            {claims.map(c => (
              <tr key={c.id} data-testid={`claim-row-${c.id}`}>
                <td><div className="font-display font-semibold">{c.employee_name}</div><div className="text-[10px] text-slate-400 font-mono">{c.emp_code}</div></td>
                <td>{c.relationship}{c.relative_name && <span className="text-[11px] text-slate-500 block">{c.relative_name}</span>}</td>
                <td>{c.wedding_date}</td>
                <td className="font-display font-bold">₹{c.amount?.toLocaleString('en-IN')}</td>
                <td><span className={`inline-block text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_COLORS[c.status]}`}>{c.status}</span></td>
                <td className="text-[11px] text-slate-500">{formatDate(c.submitted_at)}</td>
                <td className="text-right">
                  {isHRAdmin && c.status === 'Pending' && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => decide(c.id, 'approve')} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-sm" title="Approve" data-testid={`approve-claim-${c.id}`}><CheckCircle2 size={14} /></button>
                      <button onClick={() => decide(c.id, 'reject')} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm" title="Reject"><XCircle size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="marriage-form-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">New Marriage Gift Claim</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="label">Employee *</label>
                <EmployeePicker value={form.employee_id} onChange={v => setForm(f => ({ ...f, employee_id: v }))} testid="claim-emp" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Relationship *</label>
                  <select className="input" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="claim-relationship">
                    {RELATIONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Wedding Date *</label>
                  <input type="date" className="input" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} />
                </div>
              </div>
              {form.relationship !== 'Self' && (
                <div>
                  <label className="label">Relative's Name *</label>
                  <input className="input" value={form.relative_name} onChange={(e) => setForm({ ...form, relative_name: e.target.value })} />
                </div>
              )}
              {form.relationship === 'Self' && (
                <div>
                  <label className="label">Spouse (if also ELR Industries employee)</label>
                  <EmployeePicker value={form.spouse_employee_id} onChange={v => setForm(f => ({ ...f, spouse_employee_id: v }))} testid="claim-spouse" />
                  <div className="text-[11px] text-slate-500 mt-1">If selected, a mirror claim is auto-created for the spouse so both receive ₹11,000.</div>
                </div>
              )}
              <div>
                <label className="label">Wedding Card / Invitation</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => uploadCard(e.target.files?.[0])} disabled={uploading} data-testid="claim-upload-card" className="text-xs" />
                {form.wedding_card_url && <div className="text-[11px] text-emerald-700 font-mono mt-1 truncate">{form.wedding_card_url.split('/').pop()}</div>}
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="bg-pink-50 border border-pink-200 rounded-sm px-3 py-2 text-xs text-pink-900" data-testid="claim-amount-preview">
                Amount payable: <strong>₹{amount.toLocaleString('en-IN')}</strong> · Will appear as a payslip line in the next payroll cycle once HR approves.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="claim-submit">Submit Claim</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
